import { useTranslations } from '@npco/utils-translations'
import {
  Box,
  BUTTON_SIZE,
  ButtonFill,
  ButtonGhost,
  Flex,
  Heading,
  InfoBox,
  INFOBOX_VARIANT,
  RadioGroupBasic,
  RadioListCard,
  showErrorToast,
  showSuccessToast,
} from '@npco/zeller-design-system'
import { Form, Formik, FormikHelpers } from 'formik'

import { SettingsLeavingRoutePromptGuard } from 'components/RouteLeavingPromptGuard'
import { SpinnerWrapped } from 'components/Spinner'
import { ToggleFormField, ToggleLabel } from 'components/ToggleFormField'
import { translationsShared } from 'translations'

import { useGetItemSettings } from '../../hooks/useGetItemSettings'
import { useItemsAnalytics } from '../../hooks/useItemsAnalytics'
import { useGetItemCountForGstSettingQuery } from './graphql/getItemCountForGstSetting.generated'
import { useUpdateItemSettingsMutation } from './graphql/updateItemSettings.generated'
import {
  ITEM_TAX_APPLICABLE_FIELD,
  ITEM_TAX_INCLUSIVE_FIELD,
} from './ItemsSettingsForm.constants'
import { translations } from './ItemsSettingsForm.i18n'
import { Description, RadioButtonWrapper } from './ItemsSettingsForm.styled'
import { ItemsSettingsFormFields } from './ItemsSettingsForm.types'

export const ItemsSettingsForm = () => {
  const { trackGeneralSettingsUpdated } = useItemsAnalytics()
  const t = useTranslations(translations)
  const tShared = useTranslations(translationsShared)
  const { isLoading: isLoadingSettings, settings } = useGetItemSettings()
  const [updateItemSettings, { loading: isUpdatingSettings }] =
    useUpdateItemSettingsMutation()
  const { data, loading: isLoading } = useGetItemCountForGstSettingQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      gstEnabled: true,
    },
  })

  const itemCountForGstSetting = data?.getItemCountForGstSetting ?? 0
  const isLoadingItemCountForGstSetting =
    data?.getItemCountForGstSetting === undefined ? isLoading : false

  const handleFormSubmit = async (
    values: ItemsSettingsFormFields,
    { resetForm }: FormikHelpers<ItemsSettingsFormFields>
  ) => {
    try {
      await updateItemSettings({
        variables: {
          input: {
            itemsApplyTax: values.isTaxApplicable,
            itemsTaxInclusive: values.isTaxInclusive,
          },
        },
      })

      trackGeneralSettingsUpdated(values)

      resetForm({ values })
      showSuccessToast(t('successNotification'))
    } catch (e) {
      showErrorToast(t('errorNotification'))
    }
  }

  if (isLoadingSettings || isLoadingItemCountForGstSetting) {
    return <SpinnerWrapped variant="top" />
  }

  const initialValues: ItemsSettingsFormFields = {
    isTaxApplicable: Boolean(settings?.itemsApplyTax),
    isTaxInclusive: Boolean(settings?.itemsTaxInclusive),
  }

  return (
    <Formik initialValues={initialValues} onSubmit={handleFormSubmit}>
      {({ setFieldValue, values, handleSubmit, resetForm }) => {
        const shouldShowGstUpdateInfoBox =
          Boolean(itemCountForGstSetting) &&
          settings?.itemsTaxInclusive !== values.isTaxInclusive

        return (
          <Form onSubmit={handleSubmit}>
            <SettingsLeavingRoutePromptGuard />

            {isUpdatingSettings ? (
              <SpinnerWrapped variant="top" />
            ) : (
              <Flex mt="16px" gridGap="40px" flexDirection="column">
                <Heading.H3>{t('heading')}</Heading.H3>
                <Box>
                  <ToggleFormField
                    label={t('taxApplicableLabel')}
                    marginBottom="16px"
                    name={ITEM_TAX_APPLICABLE_FIELD}
                    value={false}
                  />
                  <Description as="div">
                    {t('taxApplicableDescription')}
                  </Description>
                </Box>
                <Box>
                  <ToggleLabel>{t('taxPriceLabel')}</ToggleLabel>
                  <Description as="div">{t('taxPriceDescription')}</Description>
                  <RadioButtonWrapper>
                    <RadioGroupBasic
                      gap="8px"
                      isVerticalDisplay
                      name={ITEM_TAX_INCLUSIVE_FIELD}
                      onChange={(value) => {
                        setFieldValue(
                          ITEM_TAX_INCLUSIVE_FIELD,
                          value === 'true'
                        )
                      }}
                      value={values.isTaxInclusive.toString()}
                      radioElementMarginBottom="8px"
                    >
                      <RadioListCard
                        id="gst-included"
                        title={t('taxInclusive')}
                        value="true"
                      />
                      <RadioListCard
                        id="gst-excluded"
                        title={t('taxExclusive')}
                        value="false"
                      />
                    </RadioGroupBasic>
                  </RadioButtonWrapper>
                  {shouldShowGstUpdateInfoBox && (
                    <Box mt="24px">
                      <InfoBox variant={INFOBOX_VARIANT.NEGATIVE}>
                        <InfoBox.Message>
                          {t(
                            values.isTaxInclusive
                              ? 'taxInclusiveInfoBox'
                              : 'taxExcludedInfoBox',
                            {
                              itemsCount: itemCountForGstSetting,
                            }
                          )}
                        </InfoBox.Message>
                      </InfoBox>
                    </Box>
                  )}
                </Box>

                <Box
                  display="grid"
                  gridGap="24px"
                  gridTemplateColumns="156px 110px"
                >
                  <ButtonFill size={BUTTON_SIZE.MEDIUM} type="submit">
                    {tShared('save')}
                  </ButtonFill>
                  <ButtonGhost
                    size={BUTTON_SIZE.MEDIUM}
                    onClick={() => resetForm()}
                  >
                    {tShared('cancel')}
                  </ButtonGhost>
                </Box>
              </Flex>
            )}
          </Form>
        )
      }}
    </Formik>
  )
}
